import React, { useState, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import './TCITestForm.css';

// Mutación ajustada para no seleccionar subcampos, ya que retorna un String
const GENERAR_PERFIL_TCI = gql`
  mutation GenerarPerfilTCI(
    $postId: String!
    $usernameAut: String!
    $respuestas: [Int!]!
    $primenombre: String!
    $apellido: String!
    $fecha: String!
    $useremail: String!
    $emailOwner: String!
  ) {
    generarPerfilTCI(
      postId: $postId
      usernameAut: $usernameAut
      respuestas: $respuestas
      primenombre: $primenombre
      apellido: $apellido
      fecha: $fecha
      useremail: $useremail
      emailOwner: $emailOwner
    )
  }
`;

const preguntasOriginales = [
  { texto: 'Me gusta probar cosas nuevas incluso si implican algún riesgo.', dimension: 'NS' },
  { texto: 'Me aburro fácilmente cuando hago las mismas actividades durante mucho tiempo.', dimension: 'NS' },
  { texto: 'Me preocupo con frecuencia por lo que podría salir mal.', dimension: 'HA' },
  { texto: 'Me siento incómodo en situaciones nuevas o desconocidas.', dimension: 'HA' },
  { texto: 'Necesito sentir que los demás me valoran para sentirme bien conmigo mismo.', dimension: 'RD' },
  { texto: 'A menudo busco aprobación cuando tomo decisiones importantes.', dimension: 'RD' },
  { texto: 'Aunque algo sea muy difícil, sigo insistiendo hasta lograrlo.', dimension: 'P' },
  { texto: 'Me esfuerzo al máximo incluso cuando los resultados tardan en llegar.', dimension: 'P' },
  { texto: 'Soy capaz de mantenerme firme en mis decisiones incluso cuando enfrento obstáculos.', dimension: 'SD' },
  { texto: 'Me siento responsable de mis propias acciones y elecciones.', dimension: 'SD' },
  { texto: 'Trato de comprender a las personas incluso cuando no estoy de acuerdo con ellas.', dimension: 'C' },
  { texto: 'Me esfuerzo por ser amable y justo con los demás.', dimension: 'C' },
  { texto: 'A menudo siento que hay una conexión entre todos los seres vivos.', dimension: 'ST' },
  { texto: 'Me considero una persona espiritual, aunque no necesariamente religiosa.', dimension: 'ST' }
];

function TCITestFormLink({ username, nombre, apellido, fecha, email, emailOwner, postId, setIsDisabledAfterSubmit }) {
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState(Array(preguntasOriginales.length).fill(null));
  const [testCompletado, setTestCompletado] = useState(false);
  const [generarPerfil, { loading, error }] = useMutation(GENERAR_PERFIL_TCI);

  useEffect(() => {
    const mezcladas = [...preguntasOriginales]
      .sort(() => Math.random() - 0.5)
      .reduce((acc, actual) => {
        const insertPos = acc.findIndex(p => p.dimension === actual.dimension);
        if (insertPos === -1) return [...acc, actual];
        const mitad = Math.floor(acc.length / 2);
        acc.splice(mitad, 0, actual);
        return acc;
      }, []);
    setPreguntas(mezcladas);
  }, []);

  const handleChange = (index, value) => {
    const nuevas = [...respuestas];
    nuevas[index] = parseInt(value, 10);
    setRespuestas(nuevas);
  };

  const handleSubmit = async (e) => {
    setIsDisabledAfterSubmit(true)
    e.preventDefault();
    const respuestasConDimensiones = preguntas.map((p, idx) => ({
      dimension: p.dimension,
      valor: respuestas[idx]
    }));
    const ordenDimensiones = ['NS', 'HA', 'RD', 'P', 'SD', 'C', 'ST'];
    const respuestasOrdenadas = ordenDimensiones.flatMap(dim =>
      respuestasConDimensiones.filter(r => r.dimension === dim).map(r => r.valor)
    );
    const [yyyy, mm, dd] = fecha.split('-');
    const fechaFormateada = `${dd}${mm}${yyyy}`;

    await generarPerfil({
      variables: {
        postId,
        usernameAut: username,
        respuestas: respuestasOrdenadas,
        primenombre: nombre,
        apellido,
        fecha: fechaFormateada,
        useremail: email,
        emailOwner: emailOwner
      }
    });

    setTestCompletado(true);
  };

  const formularioCompleto = respuestas.every(r => typeof r === 'number');

  return (
    <div className="tci-container">
      {!testCompletado ? (
        <>
          <h5 className="tci-title">Instrucciones del Test TCI</h5>
          <ul className="tci-instructions">
            <li>Responde usando una escala de 1 a 5:</li>
            <li><strong>1</strong>: No estoy de acuerdo</li>
            <li><strong>2</strong>: Poco en desacuerdo</li>
            <li><strong>3</strong>: Neutral</li>
            <li><strong>4</strong>: Poco de acuerdo</li>
            <li><strong>5</strong>: Completamente de acuerdo</li>
          </ul>

          <div className="tci-scroll">
            <form onSubmit={handleSubmit} className="tci-form">
              {preguntas.map((pregunta, idx) => {
                const incompleta =
                  respuestas.some((r, i) => i > idx && r !== null) &&
                  respuestas[idx] === null;
                return (
                  <div
                    key={idx}
                    className={`tci-question-block ${incompleta ? 'tci-incompleta' : ''}`}
                  >
                    <label className="tci-label">{pregunta.texto}</label>
                    <div className="tci-radio-group">
                      {[1, 2, 3, 4, 5].map(val => (
                        <label key={val} className="tci-radio-option">
                          <input
                            type="radio"
                            name={`pregunta-${idx}`}
                            value={val}
                            checked={respuestas[idx] === val}
                            onChange={() => handleChange(idx, val)}
                          />
                          <span>{val}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}

              <div style={{ paddingBottom: '2rem' }}>
                <button type="submit" className="tci-button" disabled={!formularioCompleto || loading}>
                  {loading ? 'Enviando...' : 'Generar Perfil'}
                </button>
              </div>
            </form>
          </div>
          {error && <p className="tci-error">Error: {error.message}</p>}
        </>
      ) : (
        <div className="loading-text">
          <p>Gracias por completar el test TCI, Puedes cerrar la pestaña del navegador!</p>
        </div>
      )}
    </div>
  );
}

export default TCITestFormLink;

